<div class="container-fluid complyon-page audit-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell audit-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title mb-2 text-l">Audit readiness</h1>
      <p class="complyon-subtitle mb-0 text-s">Prepare evidence packs, track findings, assign corrective actions and management responses.</p>
    </section>

    <section class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div class="d-flex flex-wrap align-items-center gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== undefined" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn doa-reset-btn d-inline-flex align-items-center gap-1">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'plan'">
      <section class="row g-3 mb-3">
        <div v-for="card in plan_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div
              class="complyon-stat-value text-l"
              :class="card.accent === 'red' ? 'text-danger' : card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-dark'"
            >
              {{ card.value }}
            </div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 pt-3">
          <div class="min-w-0">
            <div class="complyon-section-title text-s">AGSA audit readiness - FY2025/26</div>
            <div class="complyon-section-subtitle text-xs">Auditor-General of South Africa - 01 Jun - 31 Aug 2026</div>
          </div>
        </div>

        <div class="px-3 pb-3 pt-2">
          <div class="rounded-4 border border-info-subtle" style="background:#eefbf8;">
            <div class="row g-0 align-items-stretch">
              <div class="col-12 col-xl-3 p-3">
                <div class="text-success fw-semibold text-uppercase text-xs">AGSA audit readiness - FY2025/26</div>
                <div class="complyon-stat-value text-l mt-2" style="font-size: 3rem; line-height: 1;">70%</div>
                <div class="complyon-stat-note text-xs mt-2">Target: clean audit - prior year: unqualified with findings</div>
              </div>
              <div class="col-12 col-xl-9 p-3">
                <div v-for="row in readiness_rows" :key="row.label" class="d-flex align-items-center gap-3 mb-2">
                  <div class="audit-readiness-label text-xs flex-shrink-0">{{ row.label }}</div>
                  <div class="progress flex-grow-1 rounded-pill" style="height: 6px; background:#eff3f9;">
                    <div class="progress-bar rounded-pill" :style="{ width: `${row.value}%`, backgroundColor: row.color }"></div>
                  </div>
                  <div class="audit-readiness-value text-xs text-end flex-shrink-0">{{ row.value }}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 py-3">
          <div>
            <div class="complyon-section-title text-s">Audit plan &amp; scope</div>
            <div class="complyon-section-subtitle text-xs">External (AGSA) and internal audit engagements with scope, fieldwork dates and readiness.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_new_engagement_modal">
            <i class="bi bi-plus-lg"></i>
            <span>New engagement</span>
          </button>
        </div>

        <div class="px-3 pb-3">
          <div class="row g-3">
            <div v-for="engagement in engagements" :key="engagement.code" class="col-12 col-lg-4">
              <article
                class="complyon-card rounded-4 p-3 h-100 audit-engagement-card"
                :class="{ 'is-active': active_engagement_drawer === engagement.code }"
                role="button"
                tabindex="0"
                @click="open_engagement_drawer(engagement.code)"
                @keyup.enter="open_engagement_drawer(engagement.code)"
                @keyup.space.prevent="open_engagement_drawer(engagement.code)"
              >
                <div class="d-flex align-items-start justify-content-between gap-3">
                  <div class="min-w-0">
                    <div class="text-xs text-muted">{{ engagement.code }} - {{ engagement.source }}</div>
                    <div class="fw-semibold mt-1">{{ engagement.title }}</div>
                    <div class="complyon-stat-note text-xs mt-1">{{ engagement.owner }} - {{ engagement.period }}</div>
                  </div>
                  <span class="badge rounded-pill" :class="engagement.status_class">{{ engagement.stage }}</span>
                </div>

                <div class="d-flex align-items-center justify-content-between gap-2 mt-3">
                  <div class="text-xs text-uppercase text-muted">Readiness</div>
                  <div class="fw-semibold">{{ engagement.readiness }}%</div>
                </div>
                <div class="progress mt-2" style="height:4px;background:#eff3f9;">
                  <div class="progress-bar rounded-pill" :style="{ width: `${engagement.readiness}%`, backgroundColor: engagement.readiness >= 80 ? '#1c8b58' : '#c7651f' }"></div>
                </div>

                <div class="d-flex flex-wrap gap-2 mt-3">
                  <span v-for="topic in engagement.topics" :key="`${engagement.code}-${topic}`" class="text-xs text-muted">{{ topic }}</span>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

    </div>

    <div v-else-if="active_tab === 'requests'">
      <section class="row g-3 mb-3">
        <div v-for="card in request_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div
              class="complyon-stat-value text-l"
              :class="card.accent === 'red' ? 'text-danger' : card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-dark'"
            >
              {{ card.value }}
            </div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Evidence requests (RFI)</div>
            <div class="complyon-section-subtitle text-xs">Requests for information from AGSA and internal audit - owner, due date and submission status.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_new_request_modal">
            <i class="bi bi-plus-lg"></i>
            <span>New request</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Evidence requested</th>
                <th class="text-xs">Area</th>
                <th class="text-xs">Requested by</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Due</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in request_rows"
                :key="row.ref"
                class="audit-request-row"
                :class="{ 'table-row': row.ref === 'RFI-014' }"
                role="button"
                tabindex="0"
                @click="open_edit_request_modal(row.ref)"
                @keyup.enter="open_edit_request_modal(row.ref)"
                @keyup.space.prevent="open_edit_request_modal(row.ref)"
              >
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td>
                  <div class="fw-semibold">{{ row.requested }}</div>
                  <div class="complyon-stat-note text-xs mt-1">{{ row.note }}</div>
                </td>
                <td>{{ row.area }}</td>
                <td>{{ row.requested_by }}</td>
                <td>{{ row.owner }}</td>
                <td class="fw-semibold">{{ row.due }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'findings'">
      <section class="row g-3 mb-3">
        <div v-for="card in findings_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div
              class="complyon-stat-value text-l"
              :class="card.accent === 'red' ? 'text-danger' : card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-dark'"
            >
              {{ card.value }}
            </div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Audit finding register</div>
            <div class="complyon-section-subtitle text-xs">Every finding with category, rating, owner, due date and lifecycle status. Open for the full management action plan.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_new_finding_modal">
            <i class="bi bi-plus-lg"></i>
            <span>Raise finding</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Finding</th>
                <th class="text-xs">Category</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Due</th>
                <th class="text-xs">Rating</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in findings_rows"
                :key="row.ref"
                class="audit-finding-row"
                :class="{ 'table-row': row.ref === 'AFS-2026-001' }"
                role="button"
                tabindex="0"
                @click="open_finding_drawer(row.ref)"
                @keyup.enter="open_finding_drawer(row.ref)"
                @keyup.space.prevent="open_finding_drawer(row.ref)"
              >
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td>
                  <div class="fw-semibold">{{ row.finding }}</div>
                  <div v-if="row.repeat" class="d-inline-flex align-items-center gap-1 mt-1 badge rounded-pill bg-danger-subtle text-danger-emphasis text-xs">
                    <i class="bi bi-arrow-repeat"></i>
                    <span>Repeat</span>
                  </div>
                </td>
                <td>{{ row.category }}</td>
                <td>{{ row.owner }}</td>
                <td class="fw-semibold">{{ row.due }}</td>
                <td>
                  <span
                    class="badge rounded-pill"
                    :class="row.rating === 'Material' || row.rating === 'Significant' ? 'bg-danger-subtle text-danger-emphasis' : row.rating === 'Moderate' ? 'bg-warning-subtle text-warning-emphasis' : 'bg-light text-secondary'"
                  >
                    {{ row.rating }}
                  </span>
                </td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'pack'">
      <section class="complyon-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 pt-3">
          <div>
            <div class="complyon-section-title text-s">Audit pack generation</div>
            <div class="complyon-section-subtitle text-xs">Assemble a complete, AGSA-ready audit file - plan, findings, action plans, evidence and sign-off - in one pack.</div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <select v-model="selected_audit_pack_ref" class="form-select form-select-sm rounded-3" style="min-width: 260px;">
              <option v-for="engagement in engagements" :key="`pack-${engagement.code}`" :value="engagement.code">{{ engagement.code }} - {{ engagement.title }}</option>
            </select>
            <button type="button" class="btn config-navbar-primary-action finance-action-btn">
              <i class="bi bi-download"></i>
              <span>Generate pack</span>
            </button>
          </div>
        </div>

        <div class="px-3 py-3 border-top border-light-subtle">
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div>
                <div class="fw-semibold">{{ selected_audit_pack_engagement ? selected_audit_pack_engagement.title : 'Audit pack' }}</div>
                <div class="complyon-stat-note text-xs" v-if="selected_audit_pack_engagement">{{ selected_audit_pack_engagement.owner }} - {{ selected_audit_pack_engagement.period }}</div>
              </div>
              <div class="text-end">
                <div class="complyon-stat-value text-l">{{ sections_ready_count }}/{{ selected_audit_pack_sections.length }}</div>
                <div class="complyon-stat-note text-xs text-uppercase">Sections ready</div>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table complyon-table align-middle mb-0">
              <tbody>
                <tr v-for="section in selected_audit_pack_sections" :key="`${selected_audit_pack_ref}-${section.title}`">
                <td class="ps-3">
                  <div class="d-flex align-items-center gap-2">
                    <span class="rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0" style="width:28px;height:28px;background:#e5f7f3;color:#14806c;">
                      <i class="bi" :class="section.icon"></i>
                    </span>
                    <div>
                      <div class="fw-semibold">{{ section.title }}</div>
                      <div class="complyon-stat-note text-xs">{{ section.note }}</div>
                    </div>
                  </div>
                </td>
                <td class="pe-3 text-end">
                  <span class="badge complyon-badge text-xs" :class="section.state_class">{{ section.state }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

        <section class="complyon-panel rounded-4 overflow-hidden">
          <div class="px-3 pt-3">
            <div class="complyon-section-title text-s">Scope readiness - {{ selected_audit_pack_ref }}</div>
            <div class="complyon-section-subtitle text-xs">Audit areas in scope with findings and evidence status.</div>
          </div>

        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Audit area</th>
                <th class="text-xs">Readiness</th>
                <th class="text-xs">Findings</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Evidence</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
                <tr v-for="row in selected_scope_rows" :key="`${selected_audit_pack_ref}-${row.area}`">
                <td class="ps-3 fw-semibold">{{ row.area }}</td>
                <td>
                  <div class="d-flex align-items-center gap-3">
                    <div class="progress flex-grow-1 rounded-pill" style="height: 6px; background:#eff3f9;">
                      <div class="progress-bar rounded-pill" :style="{ width: `${row.readiness}%`, backgroundColor: row.readiness >= 80 ? '#1c8b58' : '#c7651f' }"></div>
                    </div>
                    <div class="fw-semibold">{{ row.readiness }}%</div>
                  </div>
                </td>
                <td class="fw-semibold">{{ row.findings }}</td>
                <td>{{ row.owner }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.evidence_class">{{ row.evidence }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>

  <teleport to="body">
    <div v-if="active_finding_drawer_data" class="audit-drawer-backdrop" @click.self="close_finding_drawer">
      <aside class="audit-drawer audit-finding-drawer" role="dialog" aria-modal="true" aria-labelledby="audit-finding-drawer-title">
        <div class="audit-drawer-header">
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div class="min-w-0">
              <div class="audit-drawer-kicker">Finding - {{ active_finding_drawer_data.ref }} - {{ active_finding_drawer_data.category }}</div>
              <h2 id="audit-finding-drawer-title" class="audit-drawer-title mb-1">{{ active_finding_drawer_data.title || active_finding_drawer_data.finding }}</h2>
              <div class="audit-drawer-subtitle">{{ active_finding_drawer_data.subtitle }}</div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="badge rounded-pill audit-drawer-stage-pill" :class="active_finding_drawer_data.rating === 'Material' || active_finding_drawer_data.rating === 'Significant' ? 'bg-danger-subtle text-danger-emphasis' : 'bg-warning-subtle text-warning-emphasis'">{{ active_finding_drawer_data.rating }}</span>
              <button type="button" class="btn audit-drawer-close" aria-label="Close finding drawer" @click="close_finding_drawer">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="audit-drawer-body">
          <section v-if="active_finding_drawer_data.alert" class="audit-finding-alert">
            <div class="d-flex align-items-start gap-2">
              <i class="bi bi-exclamation-circle"></i>
              <div>{{ active_finding_drawer_data.alert }}</div>
            </div>
          </section>

          <section class="audit-drawer-summary audit-finding-summary">
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Status</div>
              <div class="audit-drawer-summary-value"><span class="badge rounded-pill" :class="active_finding_drawer_data.status.class">{{ active_finding_drawer_data.status.label }}</span></div>
            </div>
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Rating</div>
              <div class="audit-drawer-summary-value">{{ active_finding_drawer_data.rating }}</div>
            </div>
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Owner</div>
              <div class="audit-drawer-summary-value">{{ active_finding_drawer_data.owner }}</div>
            </div>
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Due</div>
              <div class="audit-drawer-summary-value">{{ active_finding_drawer_data.due }}</div>
            </div>
          </section>

          <section class="audit-drawer-section">
            <div class="audit-drawer-section-title">Finding lifecycle</div>
            <div class="audit-drawer-chip-grid">
              <span
                v-for="step in active_finding_drawer_data.lifecycle || []"
                :key="step"
                class="audit-drawer-scope-chip audit-finding-lifecycle-chip"
                :class="{ 'is-active': step === active_finding_drawer_data.status.label }"
              >
                {{ step }}
              </span>
            </div>
          </section>

          <section class="audit-drawer-section">
            <div class="audit-drawer-section-title">Finding</div>
            <div class="audit-finding-copy">{{ active_finding_drawer_data.copy }}</div>
            <div class="audit-finding-detail-grid">
              <div class="audit-finding-detail-row"><span class="audit-finding-detail-label">Criteria</span><span class="audit-finding-detail-value">{{ active_finding_drawer_data.criteria }}</span></div>
              <div class="audit-finding-detail-row"><span class="audit-finding-detail-label">Root cause</span><span class="audit-finding-detail-value">{{ active_finding_drawer_data.root_cause }}</span></div>
              <div class="audit-finding-detail-row"><span class="audit-finding-detail-label">Effect</span><span class="audit-finding-detail-value">{{ active_finding_drawer_data.effect }}</span></div>
              <div class="audit-finding-detail-row"><span class="audit-finding-detail-label">Recommendation</span><span class="audit-finding-detail-value">{{ active_finding_drawer_data.recommendation }}</span></div>
            </div>
          </section>

          <section class="audit-drawer-section">
            <div class="audit-drawer-section-title">Management response</div>
            <div class="audit-finding-copy">{{ active_finding_drawer_data.management_response }}</div>
          </section>

          <section class="audit-drawer-section">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <div class="audit-drawer-section-title">Connected records</div>
            </div>
            <div class="audit-finding-record-grid">
              <div v-for="record in active_finding_drawer_data.connected_records || []" :key="`${active_finding_drawer_data.ref}-${record[0]}-${record[1]}`" class="audit-finding-record-card">
                <div class="d-flex align-items-start gap-2">
                  <span class="audit-finding-record-icon"><i class="bi" :class="finding_record_icon(record[0])"></i></span>
                  <div class="min-w-0 flex-grow-1">
                    <div class="audit-finding-record-type">{{ record[0] }}</div>
                    <div class="audit-finding-record-title">{{ record[1] }}</div>
                  </div>
                  <i class="bi bi-box-arrow-up-right audit-drawer-finding-arrow"></i>
                </div>
              </div>
            </div>
          </section>

          <section class="audit-drawer-section">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <div class="audit-drawer-section-title">Management action plan</div>
              <button type="button" class="btn audit-inline-link">+ Add action</button>
            </div>
            <div v-if="(active_finding_drawer_data.action_plan || []).length" class="audit-finding-list">
              <div v-for="action in active_finding_drawer_data.action_plan" :key="`${active_finding_drawer_data.ref}-${action[0]}`" class="audit-finding-list-card">
                <div class="d-flex align-items-start gap-2">
                  <span class="audit-finding-list-icon"><i class="bi bi-journal-check"></i></span>
                  <div class="min-w-0 flex-grow-1">
                    <div class="audit-finding-record-title">{{ action[0] }}</div>
                    <div class="audit-finding-record-meta">{{ action[1] }}</div>
                  </div>
                  <span class="badge rounded-pill" :class="finding_action_state_class(action[2])">{{ action[2] }}</span>
                </div>
              </div>
            </div>
            <div v-else class="audit-drawer-empty">No action plan set - add the corrective steps to resolve this finding.</div>
          </section>

          <section class="audit-drawer-section">
            <div class="audit-drawer-section-title">Evidence submissions</div>
            <div v-if="(active_finding_drawer_data.submissions || []).length" class="audit-finding-list">
              <div v-for="submission in active_finding_drawer_data.submissions" :key="`${active_finding_drawer_data.ref}-${submission[0]}`" class="audit-finding-list-card">
                <div class="d-flex align-items-start gap-2">
                  <span class="audit-finding-list-icon is-success"><i class="bi bi-file-earmark-check"></i></span>
                  <div class="min-w-0 flex-grow-1">
                    <div class="audit-finding-record-title">{{ submission[0] }}</div>
                    <div class="audit-finding-record-meta">{{ submission[1] }}</div>
                  </div>
                  <span class="badge rounded-pill bg-success-subtle text-success-emphasis">{{ submission[2] }}</span>
                </div>
              </div>
            </div>
            <div v-else class="audit-drawer-empty">No evidence submitted yet.</div>
          </section>

          <section class="audit-drawer-section">
            <div class="audit-drawer-section-title">Internal audit review</div>
            <div class="audit-finding-review-card">
              <div class="audit-finding-detail-row"><span class="audit-finding-detail-label">Reviewer</span><span class="audit-finding-detail-value">{{ active_finding_drawer_data.review_owner }}</span></div>
              <div class="audit-finding-detail-row"><span class="audit-finding-detail-label">Review status</span><span class="badge rounded-pill" :class="finding_action_state_class(active_finding_drawer_data.review_status)">{{ active_finding_drawer_data.review_status }}</span></div>
              <div class="audit-finding-review-actions">
                <label v-for="action in active_finding_drawer_data.review_actions || []" :key="`${active_finding_drawer_data.ref}-${action}`" class="audit-finding-radio">
                  <input type="radio" :name="`review-${active_finding_drawer_data.ref}`">
                  <span>{{ action }}</span>
                </label>
              </div>
              <div class="audit-finding-record-meta">All action plans must be implemented before internal audit can clear and close.</div>
            </div>
          </section>
        </div>

        <div class="audit-drawer-footer audit-finding-drawer-footer">
          <button type="button" class="btn audit-inline-link"><i class="bi bi-upload"></i><span>Submit evidence</span></button>
          <button type="button" class="btn audit-inline-link"><i class="bi bi-arrow-right"></i><span>Advance</span></button>
          <button type="button" class="btn audit-drawer-edit-btn ms-auto"><i class="bi bi-pencil-square"></i><span>Edit</span></button>
        </div>
      </aside>
    </div>

    <div v-if="active_engagement_drawer_data" class="audit-drawer-backdrop" @click.self="close_engagement_drawer">
      <aside class="audit-drawer" role="dialog" aria-modal="true" aria-labelledby="audit-engagement-drawer-title">
        <div class="audit-drawer-header">
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div class="min-w-0">
              <div class="audit-drawer-kicker">Audit - {{ active_engagement_drawer_data.code }} - {{ active_engagement_drawer_data.source }}</div>
              <h2 id="audit-engagement-drawer-title" class="audit-drawer-title mb-1">{{ active_engagement_drawer_data.title }}</h2>
              <div class="audit-drawer-subtitle">{{ active_engagement_drawer_data.subtitle }}</div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="badge rounded-pill audit-drawer-stage-pill" :class="engagement_stage_badge_class(active_engagement_drawer_data.stage)">{{ active_engagement_drawer_data.stage }}</span>
              <button type="button" class="btn audit-drawer-close" aria-label="Close engagement drawer" @click="close_engagement_drawer">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="audit-drawer-body">
          <section class="audit-drawer-summary">
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Fieldwork</div>
              <div class="audit-drawer-summary-value">{{ active_engagement_drawer_data.fieldwork }}</div>
            </div>
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Readiness</div>
              <div class="audit-drawer-summary-value">{{ active_engagement_drawer_data.readiness }}%</div>
            </div>
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Prior outcome</div>
              <div class="audit-drawer-summary-value">{{ active_engagement_drawer_data.prior_outcome }}</div>
            </div>
            <div class="audit-drawer-summary-item">
              <div class="audit-drawer-summary-label">Target</div>
              <div class="audit-drawer-summary-value">{{ active_engagement_drawer_data.target }}</div>
            </div>
          </section>

          <section class="audit-drawer-section">
            <div class="audit-drawer-section-title">Audit scope</div>
            <div class="audit-drawer-chip-grid">
              <div v-for="scope_item in active_engagement_drawer_data.scope" :key="scope_item" class="audit-drawer-scope-chip">
                <span class="audit-drawer-scope-chip-icon"><i class="bi bi-file-earmark-text"></i></span>
                <span>{{ scope_item }}</span>
              </div>
            </div>
          </section>

          <section class="audit-drawer-section">
            <div class="audit-drawer-section-title">Findings ({{ active_engagement_drawer_data.findings.length }})</div>
            <div v-if="active_engagement_drawer_data.findings.length" class="audit-drawer-findings-list">
              <div v-for="finding in active_engagement_drawer_data.findings" :key="finding.ref" class="audit-drawer-finding-card">
                <div class="d-flex align-items-start gap-2">
                  <span class="audit-drawer-finding-marker" :class="engagement_finding_marker_class(finding.rating)">{{ engagement_finding_marker_label(finding.rating) }}</span>
                  <div class="flex-grow-1 min-w-0">
                    <div class="audit-drawer-finding-title">{{ finding.ref }} - {{ finding.finding }}</div>
                    <div class="audit-drawer-finding-meta">{{ finding.category }} - {{ finding.status.label }}</div>
                  </div>
                  <i class="bi bi-chevron-right audit-drawer-finding-arrow"></i>
                </div>
              </div>
            </div>
            <div v-else class="audit-drawer-empty">No findings for this audit.</div>
          </section>
        </div>

        <div class="audit-drawer-footer">
          <button type="button" class="btn audit-drawer-edit-btn">
            <i class="bi bi-gear"></i>
            <span>Edit engagement</span>
          </button>
        </div>
      </aside>
    </div>

    <div v-if="show_new_engagement_modal" class="audit-modal-backdrop" @click.self="close_new_engagement_modal">
      <div class="audit-modal-shell" role="dialog" aria-modal="true" aria-labelledby="audit-engagement-modal-title">
        <div class="audit-modal-header">
          <div>
            <h2 id="audit-engagement-modal-title" class="audit-modal-title mb-1">New audit engagement</h2>
            <p class="audit-modal-subtitle mb-0">The audit itself — auditor, period, scope and readiness. Findings and evidence requests link to it.</p>
          </div>
          <button type="button" class="btn audit-modal-close" aria-label="Close new audit engagement modal" @click="close_new_engagement_modal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="audit-modal-scroll">
          <div class="audit-modal-body">
            <section class="audit-modal-section">
              <div class="audit-modal-section-title">Engagement</div>
              <div class="row g-3 mt-1">
                <div class="col-12">
                  <label class="audit-modal-label" for="audit-engagement-name">Name *</label>
                  <input id="audit-engagement-name" v-model="new_engagement_form.name" type="text" class="audit-modal-input" placeholder="e.g. AGSA regular audit — FY2026/27">
                </div>
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-type">Type</label>
                  <select id="audit-engagement-type" v-model="new_engagement_form.type" class="audit-modal-input audit-modal-select">
                    <option v-for="option in engagement_type_options" :key="option" :value="option">{{ option }}</option>
                  </select>
                </div>
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-status">Status</label>
                  <select id="audit-engagement-status" v-model="new_engagement_form.status" class="audit-modal-input audit-modal-select">
                    <option v-for="option in engagement_status_options" :key="option" :value="option">{{ option }}</option>
                  </select>
                </div>
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-auditor">Auditor</label>
                  <input id="audit-engagement-auditor" v-model="new_engagement_form.auditor" type="text" class="audit-modal-input" placeholder="e.g. Auditor-General of South Africa">
                </div>
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-contact">Lead contact</label>
                  <input id="audit-engagement-contact" v-model="new_engagement_form.lead_contact" type="text" class="audit-modal-input" placeholder="e.g. Chief Audit Executive">
                </div>
              </div>
            </section>

            <section class="audit-modal-section">
              <div class="audit-modal-section-title">Timing &amp; outcomes</div>
              <div class="row g-3 mt-1">
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-fieldwork">Fieldwork dates</label>
                  <input id="audit-engagement-fieldwork" v-model="new_engagement_form.fieldwork_dates" type="text" class="audit-modal-input" placeholder="e.g. 01 Jun – 31 Aug 2026">
                </div>
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-period">Period</label>
                  <input id="audit-engagement-period" v-model="new_engagement_form.period" type="text" class="audit-modal-input" placeholder="e.g. FY2026/27">
                </div>
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-prior">Prior outcome</label>
                  <input id="audit-engagement-prior" v-model="new_engagement_form.prior_outcome" type="text" class="audit-modal-input" placeholder="e.g. Unqualified with findings">
                </div>
                <div class="col-12 col-md-6">
                  <label class="audit-modal-label" for="audit-engagement-target">Target outcome</label>
                  <input id="audit-engagement-target" v-model="new_engagement_form.target_outcome" type="text" class="audit-modal-input" placeholder="e.g. Clean audit">
                </div>
              </div>
            </section>

            <section class="audit-modal-section">
              <div class="d-flex align-items-center justify-content-between gap-3 mb-2">
                <div class="audit-modal-section-title mb-0">Readiness</div>
                <div class="audit-modal-range-value">{{ new_engagement_form.readiness }}%</div>
              </div>
              <input v-model="new_engagement_form.readiness" type="range" min="0" max="100" step="1" class="form-range audit-modal-range">
            </section>

            <section class="audit-modal-section">
              <div class="audit-modal-section-title">Scope</div>
              <div class="row g-3 mt-1">
                <div class="col-12">
                  <label class="audit-modal-label" for="audit-engagement-scope-areas">Scope areas</label>
                  <textarea id="audit-engagement-scope-areas" v-model="new_engagement_form.scope_areas" class="audit-modal-input audit-modal-textarea" rows="4" placeholder="One area per line, e.g.&#10;Supply chain management &amp; procurement&#10;Annual financial statements (PFMA)"></textarea>
                </div>
                <div class="col-12">
                  <label class="audit-modal-label" for="audit-engagement-scope-notes">Scope notes</label>
                  <textarea id="audit-engagement-scope-notes" v-model="new_engagement_form.scope_notes" class="audit-modal-input audit-modal-textarea" rows="3" placeholder="Clarify focus areas, exclusions and any co-source support."></textarea>
                </div>
                <div class="col-12">
                  <label class="audit-modal-label" for="audit-engagement-evidence-focus">Evidence focus</label>
                  <textarea id="audit-engagement-evidence-focus" v-model="new_engagement_form.evidence_focus" class="audit-modal-input audit-modal-textarea" rows="3" placeholder="e.g. RFQ files, declarations, privileged access reviews, performance evidence packs"></textarea>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div class="audit-modal-footer">
          <button type="button" class="btn audit-modal-cancel" @click="close_new_engagement_modal">Cancel</button>
          <button type="button" class="btn audit-modal-submit" @click="create_engagement">
            <i class="bi bi-plus-lg"></i>
            <span>Create engagement</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="show_request_modal" class="audit-modal-backdrop" @click.self="close_request_modal">
      <div class="audit-modal-shell audit-request-modal-shell" role="dialog" aria-modal="true" aria-labelledby="audit-request-modal-title">
        <div class="audit-modal-header">
          <div>
            <h2 id="audit-request-modal-title" class="audit-modal-title mb-1">{{ is_editing_request ? 'Edit evidence request' : 'New evidence request' }}</h2>
            <p class="audit-modal-subtitle mb-0">Request for information (RFI) from the audit team</p>
          </div>
          <button type="button" class="btn audit-modal-close" :aria-label="is_editing_request ? 'Close edit evidence request modal' : 'Close new evidence request modal'" @click="close_request_modal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="audit-modal-scroll">
          <div class="audit-modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="audit-modal-label" for="audit-request-requested">Evidence requested</label>
                <input id="audit-request-requested" v-model="request_form.requested" type="text" class="audit-modal-input" placeholder="e.g. RFQ evaluation records - IT renewal">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-request-audit">Audit</label>
                <select id="audit-request-audit" v-model="request_form.audit_ref" class="audit-modal-input audit-modal-select">
                  <option v-for="engagement in engagements" :key="engagement.code" :value="engagement.code">{{ engagement.code }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-request-area">Audit area</label>
                <input id="audit-request-area" v-model="request_form.area" type="text" class="audit-modal-input">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-request-requested-by">Requested by</label>
                <input id="audit-request-requested-by" v-model="request_form.requested_by" type="text" class="audit-modal-input">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-request-owner">Owner</label>
                <input id="audit-request-owner" v-model="request_form.owner" type="text" class="audit-modal-input">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-request-due">Due</label>
                <input id="audit-request-due" v-model="request_form.due" type="text" class="audit-modal-input">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-request-status">Status</label>
                <select id="audit-request-status" v-model="request_form.status" class="audit-modal-input audit-modal-select">
                  <option v-for="option in request_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div class="col-12">
                <label class="audit-modal-label" for="audit-request-note">Note</label>
                <input id="audit-request-note" v-model="request_form.note" type="text" class="audit-modal-input">
              </div>
            </div>
          </div>
        </div>

        <div class="audit-modal-footer" :class="{ 'audit-modal-footer-split': is_editing_request }">
          <button v-if="is_editing_request" type="button" class="btn audit-modal-delete" @click="delete_request">
            <i class="bi bi-trash"></i>
            <span>Delete</span>
          </button>
          <div class="d-flex align-items-center gap-3 ms-auto">
            <button type="button" class="btn audit-modal-cancel" @click="close_request_modal">Cancel</button>
            <button v-if="is_editing_request" type="button" class="btn audit-modal-submit" @click="save_request">
              <i class="bi bi-check2-square"></i>
              <span>Save</span>
            </button>
            <button v-else type="button" class="btn audit-modal-submit" @click="create_request">
              <i class="bi bi-plus-lg"></i>
              <span>Create</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="show_finding_modal" class="audit-modal-backdrop" @click.self="close_finding_modal">
      <div class="audit-modal-shell audit-finding-modal-shell" role="dialog" aria-modal="true" aria-labelledby="audit-finding-modal-title">
        <div class="audit-modal-header">
          <div>
            <h2 id="audit-finding-modal-title" class="audit-modal-title mb-1">Raise audit finding</h2>
            <p class="audit-modal-subtitle mb-0">Action plans, evidence and links are managed inside the finding.</p>
          </div>
          <button type="button" class="btn audit-modal-close" aria-label="Close raise audit finding modal" @click="close_finding_modal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="audit-modal-scroll">
          <div class="audit-modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="audit-modal-label" for="audit-finding-title">Finding title</label>
                <input id="audit-finding-title" v-model="finding_form.title" type="text" class="audit-modal-input" placeholder="e.g. RFQ evaluation records not retained">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-audit">Audit / engagement</label>
                <select id="audit-finding-audit" v-model="finding_form.audit_ref" class="audit-modal-input audit-modal-select">
                  <option v-for="engagement in engagements" :key="engagement.code" :value="engagement.code">{{ engagement.code }} - {{ engagement.title }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-category">Category</label>
                <select id="audit-finding-category" v-model="finding_form.category" class="audit-modal-input audit-modal-select">
                  <option v-for="option in finding_category_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-rating">Rating</label>
                <select id="audit-finding-rating" v-model="finding_form.rating" class="audit-modal-input audit-modal-select">
                  <option v-for="option in finding_rating_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-status">Status</label>
                <select id="audit-finding-status" v-model="finding_form.status" class="audit-modal-input audit-modal-select">
                  <option v-for="option in finding_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div class="col-12">
                <label class="audit-modal-label" for="audit-finding-detail">Finding</label>
                <textarea id="audit-finding-detail" v-model="finding_form.finding" class="audit-modal-input audit-modal-textarea" rows="3" placeholder="What was found"></textarea>
              </div>
              <div class="col-12">
                <label class="audit-modal-label" for="audit-finding-criteria">Criteria (legislation / standard)</label>
                <input id="audit-finding-criteria" v-model="finding_form.criteria" type="text" class="audit-modal-input" placeholder="e.g. PFMA s.38(1)(a)(iii); SCM Regulation">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-cause">Cause</label>
                <textarea id="audit-finding-cause" v-model="finding_form.cause" class="audit-modal-input audit-modal-textarea audit-modal-textarea-sm" rows="2"></textarea>
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-effect">Effect</label>
                <textarea id="audit-finding-effect" v-model="finding_form.effect" class="audit-modal-input audit-modal-textarea audit-modal-textarea-sm" rows="2"></textarea>
              </div>
              <div class="col-12">
                <label class="audit-modal-label" for="audit-finding-recommendation">Recommendation</label>
                <textarea id="audit-finding-recommendation" v-model="finding_form.recommendation" class="audit-modal-input audit-modal-textarea" rows="2"></textarea>
              </div>
              <div class="col-12">
                <label class="audit-modal-label" for="audit-finding-response">Management response</label>
                <textarea id="audit-finding-response" v-model="finding_form.management_response" class="audit-modal-input audit-modal-textarea" rows="2" placeholder="Accepted / not accepted + commitment"></textarea>
              </div>
              <div class="col-12">
                <div class="audit-modal-section-title">Owner &amp; due date</div>
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-owner">Finding owner</label>
                <input id="audit-finding-owner" v-model="finding_form.owner" type="text" class="audit-modal-input" placeholder="Role - name">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-accountable">Accountable</label>
                <input id="audit-finding-accountable" v-model="finding_form.accountable" type="text" class="audit-modal-input" placeholder="e.g. Accounting Officer">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-due">Due date</label>
                <input id="audit-finding-due" v-model="finding_form.due" type="text" class="audit-modal-input" placeholder="e.g. 30 Jun 2026">
              </div>
              <div class="col-12 col-md-6">
                <label class="audit-modal-label" for="audit-finding-repeat">Repeat finding?</label>
                <select id="audit-finding-repeat" v-model="finding_form.repeat" class="audit-modal-input audit-modal-select">
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="audit-modal-footer">
          <button type="button" class="btn audit-modal-cancel" @click="close_finding_modal">Cancel</button>
          <button type="button" class="btn audit-modal-submit" @click="create_finding">
            <i class="bi bi-plus-lg"></i>
            <span>Raise finding</span>
          </button>
        </div>
      </div>
    </div>
  </teleport>
</div>
